import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Avatar, Button } from '../ui';
import PremiumName from '../ui/PremiumName';
import { useUserData } from '../../hooks/useUserData';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { formatPostDate } from '../../utils/dateFormatter';
import { deleteService } from '../../services/serviceService';
import { toggleSaveService } from '../../services/savedService';
import EditServiceModal from './EditServiceModal';
import RequestServiceModal from './RequestServiceModal';

/**
 * ServiceModal - Modal especializado para mostrar detalles de servicios
 * Diferente de PostModal ya que muestra información comercial
 */
const ServiceModal = ({ service, onClose, onServiceDeleted, onServiceUpdated }) => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const { userData: authorData } = useUserData(service.userId);
  const authorName = authorData?.displayName || service.userName || 'Usuario';
  const authorIsPremium = authorData?.isPremium;
  const authorPremiumColor = authorData?.premiumNameColor || null;
  const { currentUser, userData } = useAuth();
  const { showToast } = useToast();
  const savedServiceIds = userData?.savedServices || [];
  const isSaved = currentUser && Array.isArray(savedServiceIds) && savedServiceIds.includes(service.id);
  const [showAllDescription, setShowAllDescription] = useState(false);
  const [showAllTerms, setShowAllTerms] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  
  const isOwner = currentUser && currentUser.uid === service.userId;

  const handleToggleSave = async () => {
    if (!currentUser?.uid) return;
    const result = await toggleSaveService(currentUser.uid, service.id);
    if (result.success && result.saved) {
      showToast('Servicio guardado');
    }
  };

  // Las imágenes pueden venir como strings directos o como objetos con url
  const rawMedia = service.media || service.imageUrls || [];
  const media = rawMedia.map(item => typeof item === 'string' ? item : item?.url).filter(Boolean);
  const hasMultipleMedia = media.length > 1;

  // Cerrar con ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Prevenir scroll del body
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handlePrevImage = () => {
    setImageLoaded(false);
    setCurrentImageIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setImageLoaded(false);
    setCurrentImageIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: service.caption || 'Servicio en Illustra',
          text: service.description || '',
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert('¡Enlace copiado al portapapeles!');
      }
    } catch (error) {
      console.error('Error al compartir:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este servicio?')) {
      return;
    }

    setIsDeleting(true);
    try {
      const result = await deleteService(service.id);
      if (result.success) {
        if (onServiceDeleted) {
          onServiceDeleted(service.id);
        }
        onClose();
      } else {
        alert('Error al eliminar el servicio: ' + result.error);
      }
    } catch (error) {
      console.error('Error al eliminar servicio:', error);
      alert('Error al eliminar el servicio');
    } finally {
      setIsDeleting(false);
    }
  };

  const imageUrl = media[currentImageIndex];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" role="dialog" aria-modal="true" aria-label="Detalle del servicio">
      {/* Overlay */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col md:flex-row animate-fade-in">
        {/* Botones de acción */}
        <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
          {currentUser && (
            <button
              onClick={handleToggleSave}
              className={`p-2 rounded-full transition-colors ${isSaved ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-black/50 hover:bg-black/70 text-white'}`}
              aria-label={isSaved ? 'Quitar de guardados' : 'Guardar'}
              title={isSaved ? 'Quitar de guardados' : 'Guardar'}
            >
              {isSaved ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M5 2h14a1 1 0 011 1v19.143a1 1 0 01-1.581.814L12 18.499l-6.419 4.458A1 1 0 014 22.143V3a1 1 0 011-1z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-5-7 5V5z" />
                </svg>
              )}
            </button>
          )}
          {isOwner && (
            <>
              {/* Botón editar */}
              <button
                onClick={() => setShowEditModal(true)}
                className="p-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full transition-colors"
                aria-label="Editar servicio"
                title="Editar servicio"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>

              {/* Botón eliminar */}
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Eliminar servicio"
                title="Eliminar servicio"
              >
                {isDeleting ? (
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                )}
              </button>
            </>
          )}

          {/* Botón cerrar */}
          <button
            onClick={onClose}
            className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
            aria-label="Cerrar"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Sección de imagen (izquierda) */}
        <div className="relative w-full md:w-3/5 bg-slate-950 flex items-center justify-center">
          {imageUrl ? (
            <>
              {/* Skeleton mientras carga */}
              {!imageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                </div>
              )}

              <img
                src={imageUrl}
                alt={service.caption}
                className={`max-w-full max-h-[90vh] md:max-h-full w-auto h-auto object-contain transition-opacity duration-300 ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                onLoad={() => setImageLoaded(true)}
              />

              {/* Badge de Servicio */}
              <div className="absolute top-4 left-4 px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold rounded-full shadow-lg flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Servicio
              </div>

              {/* Controles de navegación */}
              {hasMultipleMedia && (
                <>
                  <button
                    onClick={handlePrevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                    aria-label="Imagen anterior"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={handleNextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                    aria-label="Imagen siguiente"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {/* Indicadores */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {media.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setImageLoaded(false);
                          setCurrentImageIndex(index);
                        }}
                        className={`w-2 h-2 rounded-full transition-all ${
                          index === currentImageIndex
                            ? 'bg-white w-8'
                            : 'bg-white/50 hover:bg-white/75'
                        }`}
                        aria-label={`Ir a imagen ${index + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center text-gray-400 p-8">
              <svg className="w-24 h-24 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <p>Sin imagen</p>
            </div>
          )}
        </div>

        {/* Sección de detalles (derecha) */}
        <div className="w-full md:w-2/5 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6">
            {/* Header con autor */}
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200 dark:border-slate-700">
              <Link
                to={`/profile/${service.userId}`}
                className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity"
                onClick={(e) => e.stopPropagation()}
              >
                <Avatar
                  src={authorData?.photoURL || service.userPhotoURL}
                  alt={authorName}
                  fallbackText={authorName}
                  size="md"
                />
                <div className="flex-1 min-w-0">
                  <PremiumName
                    name={authorName}
                    isPremium={authorIsPremium}
                    premiumColor={authorPremiumColor}
                    nameClassName="font-semibold text-gray-900 dark:text-white"
                    iconSize="xs"
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {formatPostDate(service.createdAt)}
                  </p>
                </div>
              </Link>
            </div>

            {/* Título del servicio */}
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              {service.caption || service.title || 'Servicio sin título'}
            </h2>

            {/* Precio */}
            {service.servicePrice > 0 && (
              <div className="mb-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Precio
                  </span>
                  <div className="text-right">
                    <span className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                      ${service.servicePrice}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400 ml-1">
                      {service.currency || 'ARS'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Descripción */}
            {service.description && (
              <div className="mb-4">
                <p className={`text-gray-700 dark:text-gray-300 whitespace-pre-wrap ${
                  !showAllDescription && service.description.length > 200 ? 'line-clamp-4' : ''
                }`}>
                  {service.description}
                </p>
                {service.description.length > 200 && (
                  <button
                    onClick={() => setShowAllDescription(!showAllDescription)}
                    className="text-indigo-600 dark:text-indigo-400 text-sm font-medium hover:underline mt-2"
                  >
                    {showAllDescription ? 'Ver menos' : 'Ver más'}
                  </button>
                )}
              </div>
            )}

            {/* Categoría */}
            {service.category && (
              <div className="mb-4">
                <span className="inline-block px-3 py-1 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 text-sm rounded-full">
                  {service.categoryName || service.category}
                </span>
              </div>
            )}

            {/* Tiempo de entrega */}
            {service.deliveryTime && (
              <div className="mb-4 flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm">
                  Entrega: <strong>{service.deliveryTime}</strong>
                </span>
              </div>
            )}

            {/* Paquetes de precios (si existen) */}
            {service.prices && Array.isArray(service.prices) && service.prices.length > 0 && (
              <div className="mb-4 border-t border-gray-200 dark:border-slate-700 pt-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                  📦 Paquetes Disponibles ({service.prices.length})
                </h3>
                <div className="space-y-3">
                  {service.prices.map((pkg, index) => (
                    <div 
                      key={pkg.id || index}
                      className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-800 dark:to-slate-700 rounded-lg border border-gray-200 dark:border-slate-600"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 dark:text-white">
                            {pkg.details || pkg.name || `Paquete ${index + 1}`}
                          </h4>
                          {pkg.deliveryTime && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-1">
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {pkg.deliveryTime}
                            </p>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                            ${pkg.price}
                          </span>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {service.currency || 'ARS'}
                          </p>
                        </div>
                      </div>
                      {pkg.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                          {pkg.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Especificaciones */}
            {service.specifications && (
              <div className="mb-4 border-t border-gray-200 dark:border-slate-700 pt-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  📋 Especificaciones
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {service.specifications}
                </p>
              </div>
            )}

            {/* Términos y condiciones */}
            {service.terms && (
              <div className="mb-4 border-t border-gray-200 dark:border-slate-700 pt-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  📜 Términos y Condiciones
                </h3>
                <p className={`text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap ${
                  !showAllTerms && service.terms.length > 200 ? 'line-clamp-4' : ''
                }`}>
                  {service.terms}
                </p>
                {service.terms.length > 200 && (
                  <button
                    onClick={() => setShowAllTerms(!showAllTerms)}
                    className="text-indigo-600 dark:text-indigo-400 text-sm font-medium hover:underline mt-2"
                  >
                    {showAllTerms ? 'Ver menos' : 'Ver más'}
                  </button>
                )}
              </div>
            )}

            {/* Personalización permitida */}
            {(service.allowClientDescription !== undefined || service.allowCustomizations !== undefined) && (
              <div className="mb-4 border-t border-gray-200 dark:border-slate-700 pt-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  ⚙️ Opciones
                </h3>
                <div className="space-y-2">
                  {service.allowClientDescription && (
                    <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Acepta descripción personalizada del cliente
                    </div>
                  )}
                  {service.allowCustomizations && (
                    <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Permite personalizaciones
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Estadísticas del servicio */}
            {(service.orders > 0 || service.views > 0) && (
              <div className="mb-4 border-t border-gray-200 dark:border-slate-700 pt-4">
                <div className="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
                  {service.orders > 0 && (
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span><strong>{service.orders}</strong> pedidos</span>
                    </div>
                  )}
                  {service.views > 0 && (
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <span><strong>{service.views}</strong> vistas</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Métodos de pago del vendedor */}
            <div className="mb-4 border-t border-gray-200 dark:border-slate-700 pt-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                Métodos de pago del vendedor
              </h3>
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4">
                {(() => {
                  const pm = authorData?.paymentMethods || {};
                  const hasCvu = pm.cvu?.active && (pm.cvu?.alias?.trim() || pm.cvu?.cvu?.trim());
                  const hasPaypal = pm.paypal?.active && pm.paypal?.email?.trim();
                  const hasCrypto = pm.crypto?.active && pm.crypto?.address?.trim();
                  if (!hasCvu && !hasPaypal && !hasCrypto) {
                    return (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        El vendedor aún no ha configurado métodos de pago.
                      </p>
                    );
                  }
                  return (
                    <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                      {hasCvu && (
                        <li>
                          <span className="font-medium text-gray-900 dark:text-white">Alias / CVU:</span>{' '}
                          {[pm.cvu.alias?.trim(), pm.cvu.cvu?.trim()].filter(Boolean).join(' · ')}
                        </li>
                      )}
                      {hasPaypal && (
                        <li>
                          <span className="font-medium text-gray-900 dark:text-white">PayPal:</span>{' '}
                          {pm.paypal.email.trim()}
                        </li>
                      )}
                      {hasCrypto && (
                        <li>
                          <span className="font-medium text-gray-900 dark:text-white">
                            Cripto{pm.crypto.label?.trim() ? ` (${pm.crypto.label.trim()})` : ''}:
                          </span>{' '}
                          {pm.crypto.address.trim()}
                        </li>
                      )}
                    </ul>
                  );
                })()}
              </div>
            </div>

            {/* Botones de acción */}
            <div className="border-t border-gray-200 dark:border-slate-700 pt-4">
              {!isOwner && currentUser && (
                <Button
                  variant="gradient"
                  size="lg"
                  className="w-full mb-3"
                  onClick={() => setShowRequestModal(true)}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Solicitar Servicio
                </Button>
              )}
              
              <button
                onClick={handleShare}
                className="w-full px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors text-sm font-medium"
              >
                <svg className="w-5 h-5 inline-block mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                  />
                </svg>
                Compartir servicio
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de edición */}
      {showEditModal && (
        <EditServiceModal
          service={service}
          onClose={() => setShowEditModal(false)}
          onServiceUpdated={(updatedService) => {
            setShowEditModal(false);
            if (onServiceUpdated) {
              onServiceUpdated(updatedService);
            }
          }}
        />
      )}

      {/* Modal de solicitud de servicio */}
      {showRequestModal && (
        <RequestServiceModal
          service={{
            ...service,
            userName: authorData?.displayName || service.userName || 'Vendedor',
            userPhotoURL: authorData?.photoURL || service.userPhotoURL
          }}
          onClose={() => setShowRequestModal(false)}
          onSuccess={(order) => {
            setShowRequestModal(false);
            onClose();
            navigate('/services');
            alert('¡Solicitud enviada exitosamente! El vendedor será notificado.');
          }}
        />
      )}
    </div>
  );
};

export default ServiceModal;
